import './styles.css';
import type { ViewId } from './types';
import { conflicts } from './data/conflicts';
import { formatNumber, formatDeaths, computeCountryStats } from './utils';
import { renderMapView, destroyMapView } from './views/map';
import { renderConflictsView } from './views/conflicts-table';
import { renderTimelineView } from './views/timeline';
import { renderRegionsView } from './views/regions';
import { renderTypesView } from './views/types';
import { renderNetworkView } from './views/network';
import { renderHeatmapView } from './views/heatmap';
import { renderInsightsView } from './views/insights';
import { initTooltip } from './tooltip';
import { openAboutModal } from './about';

const app = document.getElementById('app')!;

const views: { id: ViewId; label: string }[] = [
  { id: 'map', label: 'Map' },
  { id: 'conflicts', label: 'Conflicts' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'regions', label: 'By Region' },
  { id: 'types', label: 'By Type' },
  { id: 'network', label: 'Network' },
  { id: 'heatmap', label: 'Heatmap' },
  { id: 'insights', label: 'Insights' },
];

let currentView: ViewId = 'map';
let searchQuery = '';
let searchTimeout: ReturnType<typeof setTimeout> | undefined;

function getStats() {
  const countryStats = computeCountryStats();
  const totalDeaths2025 = conflicts.reduce((s, c) => s + c.deaths2025, 0);
  const affectedCountries = countryStats.size;
  return { totalConflicts: conflicts.length, totalDeaths2025, affectedCountries };
}

function render() {
  const stats = getStats();

  app.innerHTML = `
    <header class="app-header">
      <div class="app-title"><span class="icon">⚠</span> ConflictMap</div>
      <nav class="nav-tabs" id="nav-tabs">
        ${views.map(v => `<button class="nav-tab${v.id === currentView ? ' active' : ''}" data-view="${v.id}">${v.label}</button>`).join('')}
      </nav>
      <div class="header-actions">
        <input type="text" class="search-input" id="search" placeholder="Search conflicts…" value="${searchQuery}" />
        <button class="btn-icon" id="btn-about" title="About ConflictMap">?</button>
      </div>
    </header>
    <main class="main-content" id="main-content"></main>
    <footer class="site-footer">
      ${formatNumber(stats.totalConflicts)} active conflicts · ~${formatDeaths(stats.totalDeaths2025)} fatalities (2025 est.) · ${stats.affectedCountries} countries affected
      · Data: UCDP, ACLED, Crisis Group
      · Built by <a href="https://benrichardson.dev/">benrichardson.dev</a>
    </footer>
  `;

  // Wire up nav
  const navTabs = document.getElementById('nav-tabs')!;
  navTabs.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('.nav-tab') as HTMLElement | null;
    if (!btn) return;
    const view = btn.dataset.view as ViewId;
    if (view && view !== currentView) {
      destroyMapView();
      currentView = view;
      window.location.hash = view;
      render();
    }
  });

  // Wire up search
  const searchInput = document.getElementById('search') as HTMLInputElement;
  searchInput.addEventListener('input', () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      searchQuery = searchInput.value;
      renderCurrentView();
    }, 300);
  });

  // Wire up about
  document.getElementById('btn-about')!.addEventListener('click', openAboutModal);

  renderCurrentView();
  initTooltip();
}

function renderCurrentView() {
  const main = document.getElementById('main-content')!;
  main.innerHTML = '';

  switch (currentView) {
    case 'map':
      renderMapView(main, searchQuery);
      break;
    case 'conflicts':
      renderConflictsView(main, searchQuery);
      break;
    case 'timeline':
      renderTimelineView(main);
      break;
    case 'regions':
      renderRegionsView(main);
      break;
    case 'types':
      renderTypesView(main);
      break;
    case 'network':
      renderNetworkView(main);
      break;
    case 'heatmap':
      renderHeatmapView(main);
      break;
    case 'insights':
      renderInsightsView(main);
      break;
  }
}

// Handle hash navigation
function handleHash() {
  const hash = window.location.hash.slice(1) as ViewId;
  if (views.some(v => v.id === hash)) {
    currentView = hash;
  }
}

handleHash();
window.addEventListener('hashchange', () => {
  handleHash();
  destroyMapView();
  render();
});

render();
